import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bell, Clock, Warning, CheckSquare, X,
  Archive, Calendar, Sparkle, VideoCamera, ChatCircle, FileText,
} from '@phosphor-icons/react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// D.NOTIFS.1.5 — Mapping étendu aux types Phase D + legacy sites inline.
const typeConfig = {
  // Phase D + Iter 88 P0.1 + Iter 91 J4 (helper _create_notif)
  element_fait: { icon: CheckSquare, color: 'text-[#16A34A]', bg: 'bg-[#DCFCE7]' },
  element_ignore: { icon: Archive, color: 'text-[#6B7280]', bg: 'bg-[#F3F4F6]' },
  tache_crc_assignee: { icon: FileText, color: 'text-[#2563EB]', bg: 'bg-[#DBEAFE]' },
  convocation_reunion: { icon: Calendar, color: 'text-[#DC2626]', bg: 'bg-[#FEE2E2]' },
  element_cree_ia: { icon: Sparkle, color: 'text-[#3B82F6]', bg: 'bg-[#DBEAFE]' },
  visite_terminee: { icon: VideoCamera, color: 'text-[#6B7280]', bg: 'bg-[#F3F4F6]' },
  echeance_proche: { icon: Clock, color: 'text-[#DC2626]', bg: 'bg-[#FEE2E2]' },
  // Legacy sites inline (server.py:9197/9923/14292)
  entreprise_message: { icon: ChatCircle, color: 'text-[#2563EB]', bg: 'bg-[#DBEAFE]' },
  visite_levee_demandee: { icon: VideoCamera, color: 'text-[#DC2626]', bg: 'bg-[#FEE2E2]' },
};

// Fallback si type inconnu
const defaultConfig = { icon: Bell, color: 'text-[#6B7280]', bg: 'bg-[#F3F4F6]' };

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silently fail
    }
  };

  const handleClick = async (notif) => {
    setOpen(false);
    // D.NOTIFS.1.5 — Mark as read (best-effort, ne bloque pas la navigation)
    if (notif._id && !notif.read) {
      try {
        await axios.put(
          `${API_URL}/api/notifications/${notif._id}/read`,
          {},
          { withCredentials: true },
        );
        // Optimistic update local state
        setNotifications((prev) =>
          prev.map((n) => (n._id === notif._id ? { ...n, read: true } : n))
        );
      } catch (_) {
        // Silently fail, navigate quand meme
      }
    }
    // D.NOTIFS.1.5 — Navigation via link_url backend (deeplink riche)
    // Fallback chantier_id si pas de link_url
    if (notif.link_url) {
      navigate(notif.link_url);
    } else if (notif.chantier_id) {
      navigate(`/chantiers/${notif.chantier_id}`);
    }
  };

  // Compteur badge : non-lues uniquement (pas total)
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="icon"
        className="rounded-none border-[#E0E0DB] relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-[#C00000] text-white text-xs flex items-center justify-center px-0.5"
            data-testid="notification-count"
          >
            {unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#E0E0DB] shadow-lg z-50"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E0E0DB]">
            <h3 className="text-sm font-bold text-[#2C2C2C]">Notifications</h3>
            <button onClick={() => setOpen(false)}>
              <X size={16} className="text-[#6B6B6B]" />
            </button>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-sm text-[#9A9A9A] text-center py-6">Aucune notification</p>
            ) : (
              notifications.map((notif) => {
                const config = typeConfig[notif.type] || defaultConfig;
                const Icon = config.icon;
                const isUrgent = notif.priority === 'urgent';
                return (
                  <button
                    key={notif._id}
                    className={`w-full text-left px-4 py-3 hover:bg-[#F5F5F3] border-b border-[#E0E0DB] last:border-0 transition-colors ${
                      !notif.read ? 'bg-[#FFFBF0]' : ''
                    }`}
                    onClick={() => handleClick(notif)}
                    data-testid={`notification-item-${notif._id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                        <Icon size={16} className={config.color} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[#2C2C2C] truncate">{notif.title}</p>
                          {isUrgent && (
                            <span className="text-[10px] font-bold text-[#C00000] uppercase tracking-wider">
                              urgent
                            </span>
                          )}
                        </div>
                        {notif.body && (
                          <p className="text-xs text-[#6B6B6B] mt-0.5">{notif.body}</p>
                        )}
                        {notif.chantier_name && (
                          <p className="text-[10px] text-[#9A9A9A] mt-1">{notif.chantier_name}</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
